import { useState, Fragment, useRef, useEffect } from "react";
import { useStore } from "../../contexts/StoreContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useNotification } from "../../contexts/NotificationContext";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../../contexts/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  applyPassExpiration,
  previewPassExpiration,
  updatePassLayout,
  createPassLayout,
  deletePassLayout,
  createForm,
} from "../../api/mutations";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPassLayout,
  fetchPasses,
  fetchForms,
  fetchStoreSetup,
} from "../../api/queries";
import {
  stampIconSource,
  getCropModalTitle,
  validateMilestonesForSave,
  validateTierForSave,
  validateCouponForSave,
  openMilestoneIconCrop,
} from "./passEditorHelpers";
import defaultBackground from "../../assets/background.png";
import TrialEndedAlert from "../../components/TrialEndedAlert";
import ImageCropModal, { CROP_TYPES } from "../../components/ImageCropModal";
import {
  generateStampedImages,
  createPreviewUrls,
  appendStampedImages,
  generatePreviewImage,
} from "../../services/imageService";
import PassPreview from "./components/PassPreview";
import PassForm from "./components/PassForm";
import RecoverableBoundary from "../../components/RecoverableBoundary";
import { convertKeysToCamelCase } from "../../utils/caseConverter";
import {
  extractMilestoneIconUploads,
  rewardFieldPreview,
  newMilestoneId,
  withMilestoneIds,
  sortMilestonesByPosition,
  walletLabels,
  walletLabelsSignature,
} from "../../utils/passUtils";
import Header from "../../components/Header";
import Button from "../../components/Button";
import ExpirationHoldersBanner from "./components/ExpirationHoldersBanner";
import { useExpiryDecision } from "./useExpiryDecision";

export default function EditPass({ id: propId, setupMode = false }) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  // Owned here, not in PassForm: both save buttons go through
  // handleSaveLayout, which is the only place that can see every attempt.
  const [saveAttempted, setSaveAttempted] = useState(false);
  // Field labels the wallet itself prints — mirrored so the preview reads like
  // the real card instead of hardcoded English.
  const wl = walletLabels(t);
  const wlSignature = walletLabelsSignature(wl);
  const navigate = useNavigate();
  // Set when a save changed the expiration rule on a card that people already
  // hold: holds the server's preview of what applying it to them would do.
  // Its presence is also what keeps the page from navigating away on save, so
  // the offer has somewhere to appear.
  // Set when a save was held by the unanswered question, so the banner can say
  // why nothing happened rather than leaving the button looking broken.
  const [wasSaveBlocked, setWasSaveBlocked] = useState(false);
  const expiryBannerRef = useRef(null);
  // Set while the banner's own save is running, so the mutation's success
  // handler leaves the navigation to us — the bulk apply has to be fired
  // before the page goes anywhere.
  const deferNavigationRef = useRef(false);
  const { id: urlId } = useParams();
  const passId = propId || urlId;
  const { selectedStore } = useStore();
  const { user } = useAuth();
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [useCustomTerms, setUseCustomTerms] = useState(false);
  const [termsUrl, setTermsUrl] = useState("");
  const previewRef = useRef(null);
  // Type of the pass as originally loaded, used to detect a type switch in setup
  const originalTypeRef = useRef(null);
  // While a type change is deleting + recreating the pass, stop fetching the
  // old (now deleted) pass so we don't trigger a phantom 404 "Pass not found".
  const [isRecreating, setIsRecreating] = useState(false);

  // Fetch the current pass data
  const { data: currentPass, isLoading: isPassLoading } = useQuery({
    queryKey: ["passLayout", selectedStore?.id, passId],
    queryFn: () => fetchPassLayout(selectedStore?.id, passId),
    enabled: !!selectedStore?.id && !!passId && !isRecreating,
  });

  // Setup status, only needed to confine the type-change flow to onboarding.
  const { data: setupStatus } = useQuery({
    queryKey: ["setup-status", selectedStore?.id],
    queryFn: () => fetchStoreSetup(selectedStore?.id),
    enabled: !!selectedStore?.id && setupMode,
  });

  // Changing the card type deletes the current card (its forms/campaigns are
  // destroyed and existing customers are detached), so it's only ever allowed
  // during genuine first-time onboarding, for a brand-new card nobody uses yet.
  const canChangeType =
    setupMode &&
    setupStatus?.setup_completed === false &&
    (currentPass?.customers_count ?? 0) === 0;

  const [formData, setFormData] = useState({
    id: currentPass?.id,
    name: "Pass #1",
    type: "stamp",
    company: selectedStore?.name || "Coffee Inc.",
    companyLogo: selectedStore?.logo_url || "https://placehold.co/64x64",
    backgroundColor: "#F7F6F2",
    textColor: "#762105",
    backgroundImage: defaultBackground,
    displayName: user?.displayName || "",
    barcodeType: "qr",
    supportEmail: user?.email || "",
    initialPoints: 2,
    stampCount: 8,
    stampRewardMode: "milestone",
    milestones: [{ position: 8, label: "", _uid: newMilestoneId() }],
    validUntilDateFormat: "en-GB",
    validUntil: new Date(new Date().setMonth(new Date().getMonth() + 12))
      .toISOString()
      .slice(0, 10),
    formattedValidUntil: "",
    enableTier: false,
    tier: {
      label: "",
      value: "Bronze",
    },
    campaign: {
      label: "Reward",
      value: "Free Coffee",
    },
    targetPassId: "none",
    targetAmount: null,
    // Coupon only: grant the target card its configured initial
    // stamps/points when the coupon converts. Off by default.
    applyInitialBonusOnConversion: false,
    stampIcon: null,
    stampIconColor: "#FFFFFF",
    useCustomTerms: false,
    termsOfServiceUrl: "",
    enableCustomField: false,
    customField: null,
    selectedCustomFieldId: null,
    selectedCustomField: null,
    passExpirationType: "fixed",
    passExpirationDynamic: 1,
    passExpirationDynamicUnit: "years",
    // Dynamic by default. A fixed date goes stale: once it passes, every
    // point earned on the card is expired on creation, and nothing tells the
    // merchant. Most merchants who enable expiry want "N after earned" anyway.
    pointsExpirationType: "dynamic",
    pointsExpirationDynamic: 1,
    pointsExpirationDynamicUnit: "years",
    pointsValidUntil: new Date(new Date().setMonth(new Date().getMonth() + 12))
      .toISOString()
      .slice(0, 10),
    enablePassExpiration: false,
    enablePointsExpiration: false,
    headerField: "stamps",
    leftField: "member",
    rightField: "none",
    discount: 5,
    preview: {
      headerField: {
        label: "STAMPS",
        value: 2,
      },
      leftField: {
        label: "MEMBER",
        value: user?.displayName || "",
      },
      rightField: {
        label: "",
        value: "",
      },
    },
    enableReviewFeedbackLink: false,
    reviewFeedback: {
      label: "Review & Feedback",
      url: "",
    },
    enableReferalProgram: false,
    referralProgram: {
      type: "use",
      referrerPoints: 0,
      refereePoints: 0,
    },
    enableLinks: false,
    links: [
      {
        label: "",
        url: "",
      },
    ],
    enableCustomBackFields: false,
    customBackFields: [
      {
        label: "",
        value: "",
      },
    ],
  });

  // Update formData when currentPass is loaded
  useEffect(() => {
    if (currentPass) {
      const passData = convertKeysToCamelCase(currentPass.pass);
      originalTypeRef.current = passData.type;

      // The editor writes every field into the pass JSON on save, expiry's
      // sub-settings included, even when expiry is off — so every card saved
      // before "rolling window" became the default carries the old
      // "fixed" in it as dead weight. While expiry is off that value means
      // nothing; drop it so switching expiry on starts from today's default
      // rather than resurrecting a choice nobody made. A card with expiry on
      // keeps whatever the merchant actually picked.
      if (!passData.enablePointsExpiration) {
        delete passData.pointsExpirationType;
      }

      // Stamp cards are always milestone-driven now. A legacy single-reward
      // stamp pass (no milestones) is migrated to one milestone at its required
      // count. The reward text lives on the redemption campaign's description
      // (extended view), so seed the label/position from there to avoid wiping
      // the merchant's existing reward on the next save.
      const isStamp = passData.type === "stamp";
      const hasMilestones =
        Array.isArray(passData.milestones) && passData.milestones.length > 0;
      const redemption = (currentPass.campaigns || []).find(
        (campaign) => campaign.kind === "redemption",
      );
      const stampFields =
        isStamp && !hasMilestones
          ? {
              stampRewardMode: "milestone",
              milestones: [
                {
                  position: Number(passData.stampCount) || 8,
                  label: redemption?.description || "",
                },
              ],
            }
          : {};

      // Milestones (whether migrated from a legacy stamp or loaded from the
      // backend) carry no client id, so stamp one on for the editor to track by,
      // and sort by position so they render in stamp-progression order from the
      // first paint (the editor keeps them sorted on every edit thereafter).
      const loadedMilestones = stampFields.milestones ?? passData.milestones;

      // Stamp headers are stamp-count-only now (the milestone REWARD text
      // overflows the narrow header slot); older passes saved "rewards" /
      // "rewards_stamps" there, which would leave the selector blank and keep
      // the overflowing text on the card until re-picked.
      const headerFields =
        isStamp && ["rewards", "rewards_stamps"].includes(passData.headerField)
          ? { headerField: "stamps" }
          : {};

      setFormData((prev) => ({
        ...prev,
        ...passData,
        ...stampFields,
        ...headerFields,
        ...(loadedMilestones
          ? {
              milestones: sortMilestonesByPosition(
                withMilestoneIds(loadedMilestones),
              ),
            }
          : {}),
        company: selectedStore?.name,
        companyLogo: selectedStore?.logo_url,
        id: currentPass.id,
        backgroundImage:
          currentPass.background_image_urls[
            currentPass.background_image_urls.length - 1
          ],
        stampIcon: currentPass.stamp_icon_url,
      }));

      updatePreview();
    }
  }, [currentPass]);

  // Update mutation instead of create
  const updatePassLayoutMutation = useMutation({
    mutationFn: ({
      storeId,
      passId,
      layoutData,
      stampIcon,
      companyLogo,
      backgroundImagesFormData,
      milestoneIconsFormData,
      previewImage,
    }) => {
      return updatePassLayout({
        storeId,
        passId,
        layoutData,
        stampIcon,
        companyLogo,
        backgroundImagesFormData,
        milestoneIconsFormData,
        previewImage,
      });
    },
    onSuccess: async () => {
      // Wait for the query invalidation to complete
      await queryClient.invalidateQueries({
        queryKey: ["passes", selectedStore?.id],
      });
      // The modal flow navigates itself, once the bulk apply it may have
      // started has been handed to the server.
      if (!deferNavigationRef.current) {
        navigate(`/passes`);
      }
    },
    onError: (error) => {
      if (error.response?.status === 402) {
        setIsTrialEndedAlertOpen(true);
      }
    },
  });

  // Every rule about when the question appears, what it blocks and in what
  // order the writes happen lives in this hook, where each one is a test
  // rather than a line buried in a save handler.
  const expiry = useExpiryDecision({
    previewExpiration: ({ storeId, passId: id, layoutData }) =>
      previewPassExpiration({ storeId, passId: id, layoutData }),
    savePass: (payload) => updatePassLayoutMutation.mutateAsync(payload),
    applyExpiration: ({ storeId, passId: id }) =>
      applyPassExpiration({ storeId, passId: id }),
  });

  // Changing the card type isn't a simple update: the old pass is deleted and a
  // fresh one is created with the new type. Only reachable in setup mode.
  const recreatePassMutation = useMutation({
    mutationFn: async ({
      storeId,
      oldPassId,
      layoutData,
      stampIcon,
      companyLogo,
      backgroundImagesFormData,
      milestoneIconsFormData,
      previewImage,
    }) => {
      // Snapshot the form attached to the old pass before the delete cascades
      // it away, so we can re-create the merchant's form design on the new pass.
      const forms = await fetchForms(storeId);
      const oldForm =
        forms?.find((form) => form.pass?.id === oldPassId) ??
        forms?.[0] ??
        null;

      await deletePassLayout({ storeId, passId: oldPassId });
      const newPass = await createPassLayout({
        storeId,
        layoutData,
        stampIcon,
        companyLogo,
        backgroundImagesFormData,
        milestoneIconsFormData,
        previewImage,
      });

      // Re-attach the preserved form design to the freshly created pass.
      if (oldForm) {
        const newPassId = newPass?.id ?? (await fetchPasses(storeId))?.[0]?.id;
        if (newPassId) {
          await createForm({
            storeId,
            kind: oldForm.kind,
            passId: newPassId,
            description: oldForm.description,
            submitText: oldForm.submit_text,
            content: oldForm.content,
            backgroundColor: oldForm.background_color,
            textColor: oldForm.text_color,
            lang: oldForm.lang,
          });
        }
      }

      return newPass;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["passes", selectedStore?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["forms", selectedStore?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["setup-status", selectedStore?.id],
      });
      navigate("/setup");
    },
    onError: (error) => {
      // The old pass is gone; keep the query disabled so we don't refetch it.
      if (error.response?.status === 402) {
        setIsTrialEndedAlertOpen(true);
      }
    },
  });

  // Update preview
  const updatePreview = () => {
    const headerFieldLabels = {
      none: {
        label: "",
        value: "",
      },
      rewards: {
        label: wl.rewards,
        value: rewardFieldPreview(
          formData.initialPoints,
          formData.milestones,
          t,
        ),
      },
      stamps: {
        label: wl.stamps,
        value: formData.initialPoints,
      },
      rewards_stamps: {
        label: wl.stamps,
        value: formData.initialPoints,
      },
      points: {
        label: wl.points,
        value: formData.initialPoints,
      },
      balance: {
        label: wl.balance,
        value: formData.initialPoints,
      },
      balance_money: {
        label: wl.balance_money,
        value: `${selectedStore?.currency} ${formData.initialPoints}`,
      },
      visits: {
        label: wl.visits,
        value: formData.initialPoints,
      },
      tier: {
        label: formData.tier.label || wl.tier,
        value: formData.tier.value || "",
      },
      valid_until: {
        label: wl.valid_until,
        value: formData.formattedValidUntil || "",
      },
      discount: {
        label: wl.discount,
        value: `${formData.discount}%` || "",
      },
    };

    const secondaryFieldLabels = {
      none: {
        label: "",
        value: "",
      },
      member: {
        label: wl.member,
        value: user?.displayName || "",
      },
      birthday: {
        label: wl.birthday,
        value: "01/01/1990",
      },
      rewards: {
        label: wl.rewards,
        value: rewardFieldPreview(
          formData.initialPoints,
          formData.milestones,
          t,
        ),
      },
      stamps: {
        label: wl.stamps,
        value: formData.initialPoints,
      },
      valid_until: {
        label: wl.valid_until,
        value: formData.formattedValidUntil || "",
      },
      tier: {
        label: formData.tier.label || wl.tier,
        value: formData.tier.value || "",
      },
      balance: {
        label: wl.balance,
        value: formData.initialPoints,
      },
      balance_money: {
        label: wl.balance_money,
        value: `${selectedStore?.currency} ${formData.initialPoints}`,
      },
      visits: {
        label: wl.visits,
        value: formData.initialPoints,
      },
      points: {
        label: wl.points,
        value: formData.initialPoints,
      },
      discount: {
        label: wl.discount,
        value: `${formData.discount}%` || "",
      },
    };

    if (headerFieldLabels[formData.headerField]) {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          headerField: {
            label: headerFieldLabels[prev.headerField]?.label,
            value: headerFieldLabels[prev.headerField]?.value.toString(),
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          headerField: {
            label: formData.headerField,
            value: "{Response}",
          },
        },
      }));
    }

    if (secondaryFieldLabels[formData.leftField]) {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          leftField: {
            label: secondaryFieldLabels[prev.leftField]?.label,
            value: secondaryFieldLabels[prev.leftField]?.value.toString(),
          },
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          leftField: {
            label: formData.leftField,
            value: "{Response}",
          },
        },
      }));
    }

    if (secondaryFieldLabels[formData.rightField]) {
      console.log(formData.rightField);

      setFormData((prev) => ({
        ...prev,
        preview: {
          ...prev.preview,
          rightField: {
            label: secondaryFieldLabels[prev.rightField]?.label,
            value: secondaryFieldLabels[prev.rightField]?.value.toString(),
          },
        },
      }));
    } else {
      if (formData.type != "coupon") {
        setFormData((prev) => ({
          ...prev,
          preview: {
            ...prev.preview,
            rightField: {
              label: formData.rightField,
              value: "{Response}",
            },
          },
        }));
      }
    }
  };

  useEffect(() => {
    updatePreview();
  }, [
    formData.initialPoints,
    formData.discount,
    formData.validUntil,
    formData.validUntilDateFormat,
    formData.headerField,
    formData.leftField,
    formData.rightField,
    formData.formattedValidUntil,
    formData.enableTier,
    formData.tier,
    formData.milestones,
    wlSignature,
  ]);

  const queryClient = useQueryClient();

  const [cropModalState, setCropModalState] = useState({
    isOpen: false,
    type: CROP_TYPES.BACKGROUND,
    imageUrl: null,
    milestoneId: null,
  });

  const handleImageSelect = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropModalState({
          isOpen: true,
          type,
          imageUrl: reader.result,
        });
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  const handleCropComplete = (croppedUrl) => {
    switch (cropModalState.type) {
      case CROP_TYPES.BACKGROUND:
        setFormData((prev) => ({ ...prev, backgroundImage: croppedUrl }));
        break;
      case CROP_TYPES.LOGO:
        setFormData((prev) => ({ ...prev, companyLogo: croppedUrl }));
        break;
      case CROP_TYPES.STAMP:
        setFormData((prev) => ({ ...prev, stampIcon: croppedUrl }));
        break;
      case CROP_TYPES.MILESTONE:
        setFormData((prev) => ({
          ...prev,
          milestones: (prev.milestones || []).map((m) =>
            m._uid === cropModalState.milestoneId
              ? { ...m, icon: croppedUrl }
              : m,
          ),
        }));
        break;
    }
  };

  const handleMilestoneImageSelect = (file, milestoneId) =>
    openMilestoneIconCrop(file, milestoneId, setCropModalState);

  const handleBackgroundChange = (e) =>
    handleImageSelect(e, CROP_TYPES.BACKGROUND);
  const handleStampIconChange = (e) => handleImageSelect(e, CROP_TYPES.STAMP);

  const { data: passes } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const [stampedPreviews, setStampedPreviews] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  // The editor is long enough that the banner can be several screens above
  // whichever save button was pressed.
  const revealExpiryBanner = () => {
    expiryBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    if (expiry.isHolding) revealExpiryBanner();
  }, [expiry.isHolding]);

  const answerExpiryDecision = async (applyToExisting) => {
    deferNavigationRef.current = true;
    setIsSaving(true);

    try {
      const answered = await expiry.commit(applyToExisting);
      if (answered === null) return;

      setWasSaveBlocked(false);

      if (applyToExisting) {
        // A plain confirmation. No undo offered here: the modal already made
        // the merchant choose deliberately, and a second, time-limited chance
        // to take it back only muddies a decision that was just made on
        // purpose. The run record and its restore endpoint stay on the server
        // for support to use.
        showNotification(
          t("passes.expiry.appliedTitle"),
          t("passes.expiry.appliedBody", { count: answered.will_update ?? 0 }),
          "success",
          [],
          6000,
        );
      }

      navigate(`/passes`);
    } catch (error) {
      console.error("Error saving layout:", error);
    } finally {
      deferNavigationRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSaveLayout = async (e) => {
    e.preventDefault();

    // While the question is on screen no save may start, however many times
    // the button is pressed. Both this page's header button and the one inside
    // PassForm route through here, so neither can slip by.
    if (expiry.isHolding) {
      setWasSaveBlocked(true);
      revealExpiryBanner();
      return;
    }
    if (isSaving) return;


    // Set here rather than on a button: both this page's header button and the
    // one inside PassForm route through here, so every save path reveals the
    // inline milestone errors, including any button added later.
    setSaveAttempted(true);

    if (!validateMilestonesForSave(formData, t, showNotification)) return;
    if (!validateTierForSave(formData, t, showNotification)) return;
    if (!validateCouponForSave(formData, t, showNotification)) return;

    setIsSaving(true);

    try {
      const backgroundImagesFormData = new FormData();
      let index = 0;

      // Wait for preview element to be available in DOM
      const waitForElement = (getter, maxAttempts = 20) =>
        new Promise((resolve) => {
          let attempts = 0;
          const check = () => {
            const el = getter();
            if (el) return resolve(el);
            if (++attempts >= maxAttempts) return resolve(null);
            requestAnimationFrame(check);
          };
          check();
        });

      if (formData.type === "stamp") {
        const stampPreview =
          previewRef.current ||
          (await waitForElement(() => previewRef.current));

        if (!stampPreview) {
          window.Intercom?.(
            "showNewMessage",
            "I'm having trouble saving my card design. Can you help?",
          );
          setIsSaving(false);
          return;
        }

        // Generate stamped backgrounds
        const stampedBlobs = await generateStampedImages(
          stampPreview,
          parseInt(formData.stampCount),
          {
            stampIconColor: formData.stampIconColor,
            backgroundColor: formData.backgroundColor,
          },
        );

        // Create preview URLs
        const previewUrls = createPreviewUrls(stampedBlobs);
        setStampedPreviews(previewUrls);

        // Append stamped images to form data
        appendStampedImages(backgroundImagesFormData, stampedBlobs, index);
        index += stampedBlobs.length;
      }

      // Generate default background image if not provided
      const createDefaultBackgroundImage = (color) => {
        return new Promise((resolve) => {
          const canvas = document.createElement("canvas");
          canvas.width = 1140;
          canvas.height = 435;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = color || formData.textColor || "#762105";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(blob), "image/png");
        });
      };

      // Handle background image
      let backgroundImage;
      if (formData.backgroundImage) {
        const response = await fetch(formData.backgroundImage);
        backgroundImage = await response.blob();
      } else {
        // Create default background image with text color
        backgroundImage = await createDefaultBackgroundImage(
          formData.textColor,
        );
      }
      backgroundImagesFormData.append(
        `backgroundImage_${index}`,
        backgroundImage,
        `background-${index}.png`,
      );

      // Handle stamp icon
      let stampIcon = null;
      if (formData.stampIcon) {
        const stampResponse = await fetch(formData.stampIcon);
        stampIcon = await stampResponse.blob();
      }

      // Handle company logo
      let companyLogo = null;
      const storeLogoUrl =
        selectedStore?.logo_url || "https://placehold.co/64x64";
      if (
        formData.companyLogo !== storeLogoUrl &&
        !formData.companyLogo.startsWith("http")
      ) {
        const logoResponse = await fetch(formData.companyLogo);
        companyLogo = await logoResponse.blob();
      }

      // Generate preview image
      const previewElement = await waitForElement(
        () =>
          previewRef.current?.closest(".pass-preview-container") ||
          document.querySelector(".pass-preview-container"),
      );

      if (!previewElement) {
        window.Intercom?.(
          "showNewMessage",
          "I'm having trouble saving my card design. Can you help?",
        );
        setIsSaving(false);
        return;
      }

      const previewImageData = await generatePreviewImage(previewElement);
      const previewResponse = await fetch(previewImageData);
      const previewImage = await previewResponse.blob();

      const typeChanged =
        canChangeType &&
        originalTypeRef.current &&
        formData.type !== originalTypeRef.current;

      const { layoutData, milestoneIconsFormData } =
        await extractMilestoneIconUploads(formData);

      if (typeChanged) {
        // Stop observing the pass we're about to delete and cancel any in-flight
        // fetch, otherwise the still-mounted query refetches the deleted id and
        // the global error handler shows a "Pass not found" toast.
        setIsRecreating(true);
        await queryClient.cancelQueries({
          queryKey: ["passLayout", selectedStore?.id, passId],
        });
        await recreatePassMutation.mutateAsync({
          storeId: selectedStore.id,
          oldPassId: passId,
          layoutData,
          stampIcon,
          companyLogo,
          backgroundImagesFormData,
          milestoneIconsFormData,
          previewImage,
        });
      } else {
        const payload = {
          storeId: selectedStore.id,
          passId,
          layoutData,
          stampIcon,
          companyLogo,
          backgroundImagesFormData,
          milestoneIconsFormData,
          previewImage,
        };

        // Asked BEFORE anything is written: the card is not saved until the
        // merchant has said what should happen to the people already holding
        // it. `hold` returns true when it has opened that question.
        if (await expiry.hold(payload)) return;

        await updatePassLayoutMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error("Error saving layout:", error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stampedPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [stampedPreviews]);

  useEffect(() => {
    console.log(isSaving);
  }, [isSaving]);

  const getStampIconSource = () => stampIconSource(formData);

  const getHeaderFieldLabel = (type) => {
    if (type === "expiration") {
      return "VALID UNTIL";
    }

    if (type === "coupon") {
      return "VALID UNTIL";
    }
    if (type === "membership") {
      return "POINTS";
    }
    if (type === "stamp") {
      return "STAMPS";
    }

    return "STAMPS";
  };

  const getHeaderFieldValue = (type) => {
    if (type === "expiration") {
      return formData.validUntil;
    }

    if (type === "coupon") {
      return formData.validUntil;
    }

    if (type === "membership") {
      return 200;
    }

    if (type === "stamp") {
      return 2;
    }
    return 2;
  };

  const getInitialPoints = (type) => {
    if (type === "POINTS") {
      return 200;
    }
    if (type === "BALANCE") {
      return 0;
    }
    return 2;
  };

  // Function to get the default terms of service text with variables filled in
  const getDefaultTermsOfService = () => {
    return t("passes.form.termsOfService.defaultTerms", {
      storeName: selectedStore?.name,
      email: formData.supportEmail,
    });
  };

  // Add state for collapse
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const [isLayoutOrderOpen, setIsLayoutOrderOpen] = useState(false);

  return (
    <div className="max-w-[1800px] mx-auto space-y-6 lg:space-y-8">
      <Header
        title={t("passes.edit.title")}
        description={t("passes.edit.description")}
        rightSection={
          <Button onClick={handleSaveLayout} isSaving={isSaving}>
            {isSaving ? t("common.saving") : t("passes.edit.button")}
          </Button>
        }
      />

      {expiry.isHolding && (
        <div ref={expiryBannerRef}>
          <ExpirationHoldersBanner
            preview={expiry.decision}
            wasSaveBlocked={wasSaveBlocked}
            isSaving={isSaving}
            onApplyToExisting={() => answerExpiryDecision(true)}
            onFutureOnly={() => answerExpiryDecision(false)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] 2xl:grid-cols-2 gap-6 lg:gap-8">
        <RecoverableBoundary>
          <PassForm
            formData={formData}
            setFormData={setFormData}
            isSaving={isSaving}
            handleSaveLayout={handleSaveLayout}
            saveAttempted={saveAttempted}
            handleBackgroundChange={handleBackgroundChange}
            handleStampIconChange={handleStampIconChange}
            isAdvancedSettingsOpen={isAdvancedSettingsOpen}
            setIsAdvancedSettingsOpen={setIsAdvancedSettingsOpen}
            isLayoutOrderOpen={isLayoutOrderOpen}
            setIsLayoutOrderOpen={setIsLayoutOrderOpen}
            passes={passes}
            useCustomTerms={useCustomTerms}
            setUseCustomTerms={setUseCustomTerms}
            termsUrl={termsUrl}
            setTermsUrl={setTermsUrl}
            setIsTermsModalOpen={setIsTermsModalOpen}
            defaultBackground={defaultBackground}
            getHeaderFieldLabel={getHeaderFieldLabel}
            getHeaderFieldValue={getHeaderFieldValue}
            getInitialPoints={getInitialPoints}
            selectedStore={selectedStore}
            isEditMode={true}
            allowTypeChange={canChangeType}
            onMilestoneImageSelect={handleMilestoneImageSelect}
          />
        </RecoverableBoundary>

        <RecoverableBoundary>
          <PassPreview
            formData={formData}
            previewRef={previewRef}
            getStampIconSource={getStampIconSource}
          />
        </RecoverableBoundary>

        {/* Replace all three modals with single ImageCropModal */}
        <ImageCropModal
          isOpen={cropModalState.isOpen}
          onClose={() =>
            setCropModalState((prev) => ({ ...prev, isOpen: false }))
          }
          imageUrl={cropModalState.imageUrl}
          type={cropModalState.type}
          title={getCropModalTitle(cropModalState.type, t)}
          saveLabel={t("passes.form.background.crop.save")}
          cancelLabel={t("passes.form.background.crop.cancel")}
          onCropComplete={handleCropComplete}
        />

        <TrialEndedAlert
          isOpen={isTrialEndedAlertOpen}
          onClose={() => setIsTrialEndedAlertOpen(false)}
          subscriptionStatus={selectedStore?.subscription_status}
        />

        {/* Terms of Service Modal */}
        <Transition appear show={isTermsModalOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-60"
            onClose={() => setIsTermsModalOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/40" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 mb-4"
                    >
                      {t("passes.form.termsOfService.modalTitle")}
                    </Dialog.Title>
                    <div className="mt-2">
                      <div className="prose prose-sm prose-pre:bg-gray-100 prose-pre:text-gray-900 max-h-[60vh] overflow-y-auto">
                        <div
                          className="font-sans text-sm"
                          dangerouslySetInnerHTML={{
                            __html: getDefaultTermsOfService(),
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-200"
                        onClick={() => setIsTermsModalOpen(false)}
                      >
                        {t("common.close")}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}
